/**
 * pixi-particles — ParticleEmitter.ts
 *
 * Ported from Phaser's `Phaser.GameObjects.Particles.ParticleEmitter`
 * (MIT licensed, (c) Phaser Studio Inc), rebuilt on top of PixiJS v8's
 * `ParticleContainer` / `Particle` for high particle counts.
 *
 * Drop-in shape for this project's wrapper (see ParticleEmitterOld.ts):
 *
 *   const emitter = new ParticleEmitter(0, 0, particleContainer, config);
 *   emitter.start();
 *   gameTicker.add((t) => emitter.update(t.deltaMS));
 *   emitter.stop(true);
 *
 * Differences from the Phaser version / from the plain-Sprite port:
 *
 *  - Does NOT extend PIXI.Container. It's a plain controller object — you
 *    pass it an already-created, already-parented `PIXI.ParticleContainer`
 *    and it adds/removes `PIXI.Particle` instances into that container.
 *  - `PIXI.Particle` requires every particle in a container to share the
 *    same base texture — use a spritesheet/atlas. This class has built-in
 *    support for resolving frames out of an already-loaded `Assets`
 *    spritesheet via the `atlas` / `prefix` / `frames` / `frameRate` config
 *    fields (see `setSpritesheet` below) instead of requiring you to pass a
 *    `PIXI.Texture` directly.
 *  - IMPORTANT (Pixi requirement): by default a `ParticleContainer` only
 *    treats `position` as "dynamic" (re-uploaded every frame) — `scale`,
 *    `rotation` and `color` are treated as static for performance, and Pixi
 *    only picks up changes to them when you call `container.update()`. This
 *    class calls `container.update()` for you every tick, so animated scale
 *    / rotation / alpha / tint work correctly regardless of how the
 *    container was configured — but for best performance you should still
 *    create the container with:
 *
 *      new ParticleContainer({ dynamicProperties: { position: true, scale: true, rotation: true, color: true } })
 *
 *    if most of your emitters animate those properties every frame.
 */

import { Assets, ParticleContainer, Particle as PixiParticle, Texture } from 'pixi.js';

import { EmitterColorOp } from './EmitterColorOp';
import type { DeathZoneConfig, EmitterConfig, EmitZoneConfig } from './EmitterConfig';
import { EmitterOp } from './EmitterOp';
import { GravityWell, GravityWellConfig } from './GravityWell';
import { Particle } from './Particle';
import { ParticleBounds } from './ParticleBounds';
import { EscapeRegExp, GetFastValue, GetRandom, HasAll, HasAny, HasValue, MergeRight, Remove, StableSort } from './Utils';
import { DeathZone } from './zones/DeathZone';
import { EdgeZone } from './zones/EdgeZone';
import { RandomZone } from './zones/RandomZone';
import type { IZoneSource } from './zones/Shapes';

export var Events = {
    START: 'start',
    STOP: 'stop',
    EXPLODE: 'explode',
    COMPLETE: 'complete',
    DEATH_ZONE: 'deathzone'
};

/** Game-specific extension fields this class understands beyond EmitterConfig. */
export interface ParticleEmitterSpritesheetExtension {
    /** Assets alias for the spritesheet (tries `${atlas}-json` first, then `atlas`). */
    atlas?: string;
    /** Frame-name prefix, e.g. 'explosion_' for frames 'explosion_0', 'explosion_1', ... */
    prefix?: string;
    /** How many numbered frames to resolve. */
    frames?: number;
    /**
     * If provided, frames play as a per-particle flipbook animation over the
     * particle's lifetime (frames-per-second), instead of one random frame
     * per particle. Omit for "one random frame per particle" behaviour.
     */
    frameRate?: number;
    landscapeX?: number;
    landscapeY?: number;
    portraitX?: number;
    portraitY?: number;
}

type FullConfig = EmitterConfig & Partial<ParticleEmitterSpritesheetExtension>;

var configFastMap: (keyof EmitterConfig)[] = [
    'active',
    'advance',
    'blendMode',
    'colorEase',
    'deathCallback',
    'deathCallbackScope',
    'duration',
    'emitCallback',
    'emitCallbackScope',
    'follow',
    'frequency',
    'gravityX',
    'gravityY',
    'maxAliveParticles',
    'maxParticles',
    'name',
    'emitting',
    'particleBringToTop',
    'particleClass',
    'radial',
    'sortOrderAsc',
    'sortProperty',
    'stopAfter',
    'timeScale',
    'trackVisible'
];

var configOpMap = [
    'accelerationX',
    'accelerationY',
    'alpha',
    'angle',
    'bounce',
    'color',
    'delay',
    'hold',
    'lifespan',
    'maxVelocityX',
    'maxVelocityY',
    'moveToX',
    'moveToY',
    'quantity',
    'rotate',
    'scaleX',
    'scaleY',
    'speedX',
    'speedY',
    'tint',
    'x',
    'y'
];

type Listener = (...args: any[]) => void;

export class Emitter {
    // -- container / rendering --
    container: ParticleContainer;
    particleClass: any = Particle;

    // -- own transform (used only for the emit-zone/death-zone/bounds math
    //    in `transformPoint` — see file header note in the original Sprite
    //    port for the "why") --
    x = 0;
    y = 0;
    rotation = 0;
    scaleX = 1;
    scaleY = 1;
    visible = true;

    config: FullConfig | null = null;

    ops: Record<string, EmitterOp> = {
        accelerationX: new EmitterOp('accelerationX', 0),
        accelerationY: new EmitterOp('accelerationY', 0),
        alpha: new EmitterOp('alpha', 1),
        angle: new EmitterOp('angle', { min: 0, max: 360 }, true),
        bounce: new EmitterOp('bounce', 0),
        color: new EmitterColorOp('color'),
        delay: new EmitterOp('delay', 0, true),
        hold: new EmitterOp('hold', 0, true),
        lifespan: new EmitterOp('lifespan', 1000, true),
        maxVelocityX: new EmitterOp('maxVelocityX', 10000),
        maxVelocityY: new EmitterOp('maxVelocityY', 10000),
        moveToX: new EmitterOp('moveToX', 0),
        moveToY: new EmitterOp('moveToY', 0),
        quantity: new EmitterOp('quantity', 1, true),
        rotate: new EmitterOp('rotate', 0),
        scaleX: new EmitterOp('scaleX', 1),
        scaleY: new EmitterOp('scaleY', 1),
        speedX: new EmitterOp('speedX', 0, true),
        speedY: new EmitterOp('speedY', 0, true),
        tint: new EmitterOp('tint', 0xffffff),
        x: new EmitterOp('x', 0),
        y: new EmitterOp('y', 0)
    };

    radial = true;
    gravityX = 0;
    gravityY = 0;
    acceleration = false;
    moveTo = false;

    emitCallback: ((particle: any, emitter: any) => void) | null = null;
    emitCallbackScope: any = null;
    deathCallback: ((particle: any) => void) | null = null;
    deathCallbackScope: any = null;

    maxParticles = 0;
    maxAliveParticles = 0;
    stopAfter = 0;
    duration = 0;
    frequency = 0;
    emitting = true;
    particleBringToTop = true;
    timeScale = 1;

    emitZones: (RandomZone | EdgeZone)[] = [];
    deathZones: DeathZone[] = [];

    follow: { x: number; y: number; visible?: boolean } | null = null;
    followOffset = { x: 0, y: 0 };
    trackVisible = false;

    // -- "random texture variety" frames (Phaser-style, one per particle) --
    /** Always resolved `Texture` instances (never raw string keys) — Particle reads these directly. */
    frames: Texture[] = [];
    randomFrame = true;
    frameQuantity = 1;
    currentFrame = 0;
    frameCounter = 0;

    // -- spritesheet flipbook animation (game-specific extension) --
    // When set, and `frames` holds more than one texture, each Particle
    // advances its own fractional frame index by `frameRate` every update
    // and samples straight from `frames` (see Particle.update()) — instead
    // of `getFrame()` picking one random/cycled frame per particle.
    frameRate?: number;

    // -- orientation-based positioning (game-specific extension) --
    private _landscapeX = 0;
    private _landscapeY = 0;
    private _portraitX = 0;
    private _portraitY = 0;

    dead: Particle[] = [];
    alive: Particle[] = [];

    flowCounter = 0;
    elapsed = 0;
    stopCounter = 0;
    completeFlag = false;
    zoneIndex = 0;
    zoneTotal = 0;

    skipping = false;

    sortProperty = '';
    sortOrderAsc = true;
    sortCallback: ((a: Particle, b: Particle) => number) | null = null;

    processors: any[] = [];

    blendMode = 'normal';
    name = '';

    active = true;

    private _events: Record<string, Listener[]> = {};

    private _ma = 1;
    private _mb = 0;
    private _mc = 0;
    private _md = 1;
    private _mtx = 0;
    private _mty = 0;

    constructor(x: number, y: number, container: ParticleContainer, config?: FullConfig) {
        this.container = container;
        this.x = x || 0;
        this.y = y || 0;

        this.sortCallback = this.depthSortCallback.bind(this);

        if (config) {
            this.setConfig(config);
        }
    }

    // ------------------------------------------------------------------
    // Minimal event emitter (start / stop / explode / complete / deathzone)
    // ------------------------------------------------------------------

    on(event: string, fn: Listener): this {
        (this._events[event] || (this._events[event] = [])).push(fn);

        return this;
    }

    off(event: string, fn?: Listener): this {
        if (!this._events[event]) {
            return this;
        }

        this._events[event] = fn ? this._events[event].filter((f) => f !== fn) : [];

        return this;
    }

    emit(event: string, ...args: any[]): this {
        (this._events[event] || []).forEach((fn) => fn(...args));

        return this;
    }

    // ------------------------------------------------------------------
    // Property proxies onto `ops`
    // ------------------------------------------------------------------

    get particleX(): any {
        return this.ops.x.current;
    }
    set particleX(value: any) {
        this.ops.x.onChange(value);
    }

    get particleY(): any {
        return this.ops.y.current;
    }
    set particleY(value: any) {
        this.ops.y.onChange(value);
    }

    get accelerationX(): any {
        return this.ops.accelerationX.current;
    }
    set accelerationX(value: any) {
        this.ops.accelerationX.onChange(value);
    }

    get accelerationY(): any {
        return this.ops.accelerationY.current;
    }
    set accelerationY(value: any) {
        this.ops.accelerationY.onChange(value);
    }

    get maxVelocityX(): any {
        return this.ops.maxVelocityX.current;
    }
    set maxVelocityX(value: any) {
        this.ops.maxVelocityX.onChange(value);
    }

    get maxVelocityY(): any {
        return this.ops.maxVelocityY.current;
    }
    set maxVelocityY(value: any) {
        this.ops.maxVelocityY.onChange(value);
    }

    get speed(): any {
        return this.ops.speedX.current;
    }
    set speed(value: any) {
        this.ops.speedX.onChange(value);
        this.ops.speedY.onChange(value);
    }

    get speedX(): any {
        return this.ops.speedX.current;
    }
    set speedX(value: any) {
        this.ops.speedX.onChange(value);
    }

    get speedY(): any {
        return this.ops.speedY.current;
    }
    set speedY(value: any) {
        this.ops.speedY.onChange(value);
    }

    get moveToX(): any {
        return this.ops.moveToX.current;
    }
    set moveToX(value: any) {
        this.ops.moveToX.onChange(value);
    }

    get moveToY(): any {
        return this.ops.moveToY.current;
    }
    set moveToY(value: any) {
        this.ops.moveToY.onChange(value);
    }

    get bounce(): any {
        return this.ops.bounce.current;
    }
    set bounce(value: any) {
        this.ops.bounce.onChange(value);
    }

    get particleScaleX(): any {
        return this.ops.scaleX.current;
    }
    set particleScaleX(value: any) {
        this.ops.scaleX.onChange(value);
    }

    get particleScaleY(): any {
        return this.ops.scaleY.current;
    }
    set particleScaleY(value: any) {
        this.ops.scaleY.onChange(value);
    }

    get particleColor(): any {
        return this.ops.color.current;
    }
    set particleColor(value: any) {
        this.ops.color.onChange(value);
    }

    get colorEase(): string {
        return (this.ops.color as EmitterColorOp).easeName;
    }
    set colorEase(value: string) {
        (this.ops.color as EmitterColorOp).setEase(value);
    }

    get particleTint(): any {
        return this.ops.tint.current;
    }
    set particleTint(value: any) {
        this.ops.tint.onChange(value);
    }

    get particleAlpha(): any {
        return this.ops.alpha.current;
    }
    set particleAlpha(value: any) {
        this.ops.alpha.onChange(value);
    }

    get lifespan(): any {
        return this.ops.lifespan.current;
    }
    set lifespan(value: any) {
        this.ops.lifespan.onChange(value);
    }

    get particleAngle(): any {
        return this.ops.angle.current;
    }
    set particleAngle(value: any) {
        this.ops.angle.onChange(value);
    }

    get particleRotate(): any {
        return this.ops.rotate.current;
    }
    set particleRotate(value: any) {
        this.ops.rotate.onChange(value);
    }

    get quantity(): any {
        return this.ops.quantity.current;
    }
    set quantity(value: any) {
        this.ops.quantity.onChange(value);
    }

    get delay(): any {
        return this.ops.delay.current;
    }
    set delay(value: any) {
        this.ops.delay.onChange(value);
    }

    get hold(): any {
        return this.ops.hold.current;
    }
    set hold(value: any) {
        this.ops.hold.onChange(value);
    }

    // ------------------------------------------------------------------
    // Configuration
    // ------------------------------------------------------------------

    setConfig(config: FullConfig): this {
        if (!config) {
            return this;
        }

        this.config = config;

        var i: number, key: string;
        var ops = this.ops;

        for (i = 0; i < configOpMap.length; i++) {
            key = configOpMap[i];

            ops[key].loadConfig(config);
        }

        for (i = 0; i < configFastMap.length; i++) {
            key = configFastMap[i] as string;

            if (HasValue(config, key)) {
                (this as any)[key] = (config as any)[key];
            }
        }

        this.acceleration = this.accelerationX !== 0 || this.accelerationY !== 0;

        this.moveTo = HasAll(config, ['moveToX', 'moveToY']);

        if (HasValue(config, 'speed')) {
            ops.speedX.loadConfig(config, 'speed');
            ops.speedY.active = false;
        }

        if (HasAny(config, ['speedX', 'speedY']) || this.moveTo) {
            this.radial = false;
        }

        if (HasValue(config, 'scale')) {
            ops.scaleX.loadConfig(config, 'scale');
            ops.scaleY.active = false;
        }

        if (HasValue(config, 'callbackScope')) {
            var callbackScope = GetFastValue(config, 'callbackScope', null);

            this.emitCallbackScope = callbackScope;
            this.deathCallbackScope = callbackScope;
        }

        if (HasValue(config, 'emitZone')) {
            this.addEmitZone(config.emitZone!);
        }

        if (HasValue(config, 'deathZone')) {
            this.addDeathZone(config.deathZone!);
        }

        if (HasValue(config, 'bounds')) {
            var bounds = this.addParticleBounds(config.bounds as any);

            bounds.collideLeft = GetFastValue(config, 'collideLeft', true);
            bounds.collideRight = GetFastValue(config, 'collideRight', true);
            bounds.collideTop = GetFastValue(config, 'collideTop', true);
            bounds.collideBottom = GetFastValue(config, 'collideBottom', true);
        }

        if (HasValue(config, 'followOffset')) {
            var fo = GetFastValue(config, 'followOffset', { x: 0, y: 0 });

            this.followOffset.x = fo.x || 0;
            this.followOffset.y = fo.y || 0;
        }

        //  -- game-specific spritesheet extension --
        if (HasValue(config, 'atlas') || HasValue(config, 'texture')) {
            this.setSpritesheet(config);
        } else if (HasValue(config, 'frame')) {
            this.setEmitterFrame(config.frame);
        }

        //  -- game-specific orientation extension --
        this._landscapeX = GetFastValue(config, 'landscapeX', this.x);
        this._landscapeY = GetFastValue(config, 'landscapeY', this.y);
        this._portraitX = GetFastValue(config, 'portraitX', this.x);
        this._portraitY = GetFastValue(config, 'portraitY', this.y);

        if (HasValue(config, 'reserve')) {
            this.reserve(config.reserve!);
        }

        if (HasValue(config, 'advance')) {
            this.fastForward(config.advance!);
        }

        this.resetCounters(this.frequency, this.emitting);

        if (this.emitting) {
            this.emit(Events.START, this);
        }

        return this;
    }

    updateConfig(config: FullConfig): this {
        if (config) {
            this.setConfig(this.config ? MergeRight(this.config, config) : config);
        }

        return this;
    }

    resetCounters(frequency: number, on: boolean): void {
        this.flowCounter = frequency;
        this.frameCounter = 0;
        this.elapsed = 0;
        this.stopCounter = 0;
        this.completeFlag = !!on;
        this.zoneIndex = 0;
        this.zoneTotal = 0;
        this.currentFrame = 0;
    }

    // ------------------------------------------------------------------
    // Texture / spritesheet handling
    // ------------------------------------------------------------------

    /**
     * Resolves this emitter's texture(s) from an already-`Assets.load()`ed
     * spritesheet, via the game-specific `atlas` / `prefix` / `frames` /
     * `frameRate` config fields.
     *
     * - `atlas`: the Assets alias for the spritesheet JSON. Tries
     *   `Assets.get(`${atlas}-json`)` first (matching this project's loader,
     *   which aliases spritesheet JSON as `${key}-json`), then falls back to
     *   `Assets.get(atlas)`.
     * - `prefix` + `frames`: builds the frame list by looking for frame names
     *   like `${prefix}0`, `${prefix}00`, `${prefix}0.png`, etc. (a few
     *   common numbering/padding conventions are tried automatically).
     * - `frameRate` (optional): if set, each particle plays through the
     *   resolved frames as a flipbook animation over its lifetime instead of
     *   picking one random frame at spawn.
     *
     * You can also just call this directly instead of going through
     * `setConfig` if you want to swap the spritesheet at runtime.
     */
    setSpritesheet(config: FullConfig): this {
        var atlasKey = GetFastValue(config, 'atlas', null);
        var prefix = GetFastValue(config, 'prefix', '');
        var count = GetFastValue(config, 'frames', 1);
        var frameRate = GetFastValue(config, 'frameRate', undefined);

        var textures: Texture[];

        if (atlasKey) {
            textures = Emitter.resolveSpritesheetFrames(atlasKey, prefix, count);
        } else if (HasValue(config, 'texture')) {
            var tex = config.texture;

            textures = Array.isArray(tex) ? tex.map((t: any) => this._resolveTexture(t)) : [this._resolveTexture(tex)];
        } else {
            textures = [];
        }

        if (textures.length === 0) {
            console.warn('[ParticleEmitter] setSpritesheet() resolved no textures — particles will fail to fire until a texture is available.');
        }

        this.frameRate = frameRate;

        //  `setEmitterFrame` always populates `this.frames` with the full
        //  resolved texture list — Particle.update() reads `emitter.frames`
        //  directly for flipbook playback when `frameRate` is set, while
        //  `getFrame()` (used at emit time) still picks the starting frame.
        this.setEmitterFrame(textures);

        if (frameRate !== undefined && textures.length > 1) {
            //  Emit-time frame choice barely matters in animated mode since
            //  Particle.update() overrides `texture` from frame 0 on its very
            //  first tick — but start every particle on frame 0 either way.
            this.randomFrame = false;
            this.currentFrame = 0;
        }

        //  All particles in a PIXI.ParticleContainer must share one base
        //  texture — setting this explicitly avoids relying on "first
        //  particle added wins".
        if (textures.length > 0) {
            this.container.texture = textures[0];
        }

        return this;
    }

    /**
     * Looks up `${atlasKey}-json` (or `atlasKey`) in `PIXI.Assets`' cache and
     * builds a texture array from `${prefix}<n>` frame names for n in
     * [0, count). Several common naming/padding conventions are tried.
     */
    static resolveSpritesheetFrames(atlasKey: string, prefix: string, count: number): Texture[] {
        var sheet: any = Assets.cache.has(`${atlasKey}-json`) ? Assets.get(`${atlasKey}-json`) : Assets.cache.has(atlasKey) ? Assets.get(atlasKey) : null;

        if (!sheet || !sheet.textures) {
            console.warn(
                `[ParticleEmitter] Could not find a loaded spritesheet for atlas "${atlasKey}" (looked for "${atlasKey}-json" and "${atlasKey}"). Did you Assets.load() it first?`
            );

            return [];
        }

        var names: string[] = Object.keys(sheet.textures);
        var textures: Texture[] = [];

        for (var i = 1; i <= count; i++) {
            var candidates = [
                `${prefix}${i}`,
                `${prefix}${i}.png`,
                `${prefix}${String(i).padStart(2, '0')}`,
                `${prefix}${String(i).padStart(2, '0')}.png`,
                `${prefix}${String(i).padStart(3, '0')}`,
                `${prefix}${String(i).padStart(3, '0')}.png`,
                `${prefix}${String(i).padStart(4, '0')}`,
                `${prefix}${String(i).padStart(4, '0')}.png`
            ];

            var tex: Texture | undefined;

            for (var c = 0; c < candidates.length; c++) {
                if (sheet.textures[candidates[c]]) {
                    tex = sheet.textures[candidates[c]];
                    break;
                }
            }

            if (!tex) {
                var re = new RegExp(`^${EscapeRegExp(prefix)}0*${i}(\\.[a-z]+)?$`, 'i');
                var match = names.find((n) => re.test(n));

                if (match) {
                    tex = sheet.textures[match];
                }
            }

            if (tex) {
                textures.push(tex);
            }
        }

        if (textures.length === 0) {
            console.warn(
                `[ParticleEmitter] No frames matched prefix "${prefix}" (wanted ${count}) in atlas "${atlasKey}". Available frame names include: ${names.slice(0, 12).join(', ')}${names.length > 12 ? ', ...' : ''}`
            );
        }

        return textures;
    }

    /** @param texture A PIXI.Texture, texture key, or array of either. */
    setTexture(texture: Texture | string | (Texture | string)[]): this {
        this.frameRate = undefined;

        if (Array.isArray(texture)) {
            this.setEmitterFrame(texture);
        } else {
            this.setEmitterFrame([texture]);
        }

        return this;
    }

    private _resolveTexture(entry: Texture | string): Texture {
        if (entry instanceof Texture) {
            return entry;
        }

        return Texture.from(entry as string);
    }

    getFrame(): Texture | null {
        var frames = this.frames;
        var len = frames.length;
        var current: Texture;

        if (len === 0) {
            return null;
        } else if (len === 1) {
            current = frames[0];
        } else if (this.randomFrame) {
            current = GetRandom(frames)!;
        } else {
            current = frames[this.currentFrame];

            this.frameCounter++;

            if (this.frameCounter === this.frameQuantity) {
                this.frameCounter = 0;
                this.currentFrame++;

                if (this.currentFrame === len) {
                    this.currentFrame = 0;
                }
            }
        }

        return current;
    }

    /** Accepts textures/keys but always resolves and stores them as `Texture[]` on `this.frames`. */
    setEmitterFrame(frames: any, pickRandom = true, quantity = 1): this {
        this.randomFrame = pickRandom;
        this.frameQuantity = quantity;
        this.currentFrame = 0;

        var raw: any[] = [];

        if (Array.isArray(frames)) {
            raw = frames;
        } else if (frames instanceof Texture || typeof frames === 'string') {
            raw = [frames];
        } else if (frames && typeof frames === 'object') {
            var list = GetFastValue(frames, 'frames', null);

            if (list) {
                raw = list;
            }

            var isCycle = GetFastValue(frames, 'cycle', false);

            this.randomFrame = !isCycle;
            this.frameQuantity = GetFastValue(frames, 'quantity', quantity);
        }

        this.frames = raw.map((entry) => this._resolveTexture(entry));

        if (this.frames.length === 1) {
            this.frameQuantity = 1;
            this.randomFrame = false;
        }

        return this;
    }

    // ------------------------------------------------------------------
    // Mode / follow / orientation helpers
    // ------------------------------------------------------------------

    setRadial(value = true): this {
        this.radial = value;

        return this;
    }

    startFollow(target: { x: number; y: number; visible?: boolean }, offsetX = 0, offsetY = 0, trackVisible = false): this {
        this.follow = target;
        this.followOffset.x = offsetX;
        this.followOffset.y = offsetY;
        this.trackVisible = trackVisible;

        return this;
    }

    stopFollow(): this {
        this.follow = null;
        this.followOffset.x = 0;
        this.followOffset.y = 0;
        this.trackVisible = false;

        return this;
    }

    setPosition(x: number, y: number): this {
        this.x = x;
        this.y = y;

        return this;
    }

    /**
     * Repositions the emitter using the `landscapeX/Y` / `portraitX/Y` pair
     * from its config (the game-specific orientation extension).
     */
    setOrientation(isPortrait: boolean): this {
        return this.setPosition(isPortrait ? this._portraitX : this._landscapeX, isPortrait ? this._portraitY : this._landscapeY);
    }

    addParticleBounds(
        x: number | { x: number; y: number; w?: number; h?: number; width?: number; height?: number },
        y?: number,
        width?: number,
        height?: number,
        collideLeft?: boolean,
        collideRight?: boolean,
        collideTop?: boolean,
        collideBottom?: boolean
    ): ParticleBounds {
        if (typeof x === 'object') {
            var obj = x;

            width = HasValue(obj, 'w') ? (obj as any).w : obj.width;
            height = HasValue(obj, 'h') ? (obj as any).h : obj.height;
            y = obj.y;
            x = obj.x;
        }

        return this.addParticleProcessor(
            new ParticleBounds(x as number, y!, width!, height!, collideLeft, collideRight, collideTop, collideBottom)
        ) as ParticleBounds;
    }

    setParticleSpeed(x: number, y: number = x): this {
        this.ops.speedX.onChange(x);

        if (x === y) {
            this.ops.speedY.active = false;
        } else {
            this.ops.speedY.onChange(y);
        }

        this.radial = true;

        return this;
    }

    setParticleScale(x = 1, y: number = x): this {
        this.ops.scaleX.onChange(x);
        this.ops.scaleY.onChange(y);

        return this;
    }

    setParticleGravity(x: number, y: number): this {
        this.gravityX = x;
        this.gravityY = y;

        return this;
    }

    setParticleAlpha(value: number): this {
        this.ops.alpha.onChange(value);

        return this;
    }

    setParticleTint(value: number): this {
        this.ops.tint.onChange(value);

        return this;
    }

    setEmitterAngle(value: number): this {
        this.ops.angle.onChange(value);

        return this;
    }

    setParticleLifespan(value: number): this {
        this.ops.lifespan.onChange(value);

        return this;
    }

    setQuantity(quantity: number): this {
        this.quantity = quantity;

        return this;
    }

    setFrequency(frequency: number, quantity?: number): this {
        this.frequency = frequency;
        this.flowCounter = frequency > 0 ? frequency : 0;

        if (quantity) {
            this.quantity = quantity;
        }

        return this;
    }

    // ------------------------------------------------------------------
    // Zones
    // ------------------------------------------------------------------

    addDeathZone(config: DeathZoneConfig | DeathZoneConfig[] | DeathZone): DeathZone[] {
        var list: any[] = Array.isArray(config) ? config : [config];
        var output: DeathZone[] = [];

        for (var i = 0; i < list.length; i++) {
            var zone = list[i];

            if (zone instanceof DeathZone) {
                output.push(zone);
            } else if (typeof zone.contains === 'function') {
                output.push(new DeathZone(zone, true));
            } else {
                var type = GetFastValue(zone, 'type', 'onEnter');
                var source: IZoneSource = GetFastValue(zone, 'source', null);

                if (source && typeof source.contains === 'function') {
                    output.push(new DeathZone(source, type === 'onEnter'));
                }
            }
        }

        this.deathZones = this.deathZones.concat(output);

        return output;
    }

    removeDeathZone(zone: DeathZone): this {
        Remove(this.deathZones, zone);

        return this;
    }

    clearDeathZones(): this {
        this.deathZones.length = 0;

        return this;
    }

    addEmitZone(config: EmitZoneConfig | EmitZoneConfig[] | RandomZone | EdgeZone): (RandomZone | EdgeZone)[] {
        var list: any[] = Array.isArray(config) ? config : [config];
        var output: (RandomZone | EdgeZone)[] = [];

        for (var i = 0; i < list.length; i++) {
            var zone = list[i];

            if (zone instanceof RandomZone || zone instanceof EdgeZone) {
                output.push(zone);
            } else {
                var source: IZoneSource = GetFastValue(zone, 'source', null);

                if (source) {
                    var type = GetFastValue(zone, 'type', 'random');

                    if (type === 'random' && typeof source.getRandomPoint === 'function') {
                        output.push(new RandomZone(source));
                    } else if (type === 'edge' && typeof source.getPoints === 'function') {
                        var quantity = GetFastValue(zone, 'quantity', 1);
                        var stepRate = GetFastValue(zone, 'stepRate', 0);
                        var yoyo = GetFastValue(zone, 'yoyo', false);
                        var seamless = GetFastValue(zone, 'seamless', true);
                        var total = GetFastValue(zone, 'total', -1);

                        output.push(new EdgeZone(source, quantity, stepRate, yoyo, seamless, total));
                    }
                }
            }
        }

        this.emitZones = this.emitZones.concat(output);

        return output;
    }

    removeEmitZone(zone: RandomZone | EdgeZone): this {
        Remove(this.emitZones, zone);

        this.zoneIndex = 0;

        return this;
    }

    clearEmitZones(): this {
        this.emitZones.length = 0;
        this.zoneIndex = 0;

        return this;
    }

    getEmitZone(particle: Particle): void {
        var zones = this.emitZones;
        var len = zones.length;

        if (len === 0) {
            return;
        }

        var zone = zones[this.zoneIndex] as any;

        zone.getPoint(particle);

        if (zone.total > -1) {
            this.zoneTotal++;

            if (this.zoneTotal === zone.total) {
                this.zoneTotal = 0;
                this.zoneIndex++;

                if (this.zoneIndex === len) {
                    this.zoneIndex = 0;
                }
            }
        }
    }

    getDeathZone(particle: Particle): boolean {
        var zones = this.deathZones;

        for (var i = 0; i < zones.length; i++) {
            var zone = zones[i];

            if (zone.willKill(particle)) {
                this.emit(Events.DEATH_ZONE, this, particle, zone);

                return true;
            }
        }

        return false;
    }

    setEmitZone(zone: number | RandomZone | EdgeZone): this {
        var index: number;

        if (typeof zone === 'number' && isFinite(zone)) {
            index = zone;
        } else {
            index = this.emitZones.indexOf(zone as any);
        }

        if (index >= 0) {
            this.zoneIndex = index;
        }

        return this;
    }

    // ------------------------------------------------------------------
    // Processors
    // ------------------------------------------------------------------

    addParticleProcessor(processor: any): any {
        if (this.processors.indexOf(processor) === -1) {
            if (processor.emitter) {
                processor.emitter.removeParticleProcessor(processor);
            }

            this.processors.push(processor);
            processor.emitter = this;
        }

        return processor;
    }

    removeParticleProcessor(processor: any): any {
        var index = this.processors.indexOf(processor);

        if (index !== -1) {
            this.processors.splice(index, 1);
            processor.emitter = null;
        }

        return processor;
    }

    getProcessors(): any[] {
        return this.processors.filter((p) => p.active);
    }

    createGravityWell(config: GravityWellConfig): GravityWell {
        return this.addParticleProcessor(new GravityWell(config));
    }

    // ------------------------------------------------------------------
    // Pool / counts
    // ------------------------------------------------------------------

    reserve(count: number): this {
        if (this.maxParticles > 0) {
            var total = this.getParticleCount();

            if (total + count > this.maxParticles) {
                count = this.maxParticles - (total + count);
            }
        }

        for (var i = 0; i < count; i++) {
            this.dead.push(new this.particleClass(this));
        }

        return this;
    }

    getAliveParticleCount(): number {
        return this.alive.length;
    }
    getDeadParticleCount(): number {
        return this.dead.length;
    }
    getParticleCount(): number {
        return this.getAliveParticleCount() + this.getDeadParticleCount();
    }

    atLimit(): boolean {
        if (this.maxParticles > 0 && this.getParticleCount() >= this.maxParticles) {
            return true;
        }

        return this.maxAliveParticles > 0 && this.getAliveParticleCount() >= this.maxAliveParticles;
    }

    onParticleEmit(callback?: (particle: any, emitter: any) => void, context?: any): this {
        if (callback === undefined) {
            this.emitCallback = null;
            this.emitCallbackScope = null;
        } else {
            this.emitCallback = callback;

            if (context) {
                this.emitCallbackScope = context;
            }
        }

        return this;
    }

    onParticleDeath(callback?: (particle: any) => void, context?: any): this {
        if (callback === undefined) {
            this.deathCallback = null;
            this.deathCallbackScope = null;
        } else {
            this.deathCallback = callback;

            if (context) {
                this.deathCallbackScope = context;
            }
        }

        return this;
    }

    killAll(): this {
        var dead = this.dead;
        var alive = this.alive;

        while (alive.length > 0) {
            var particle = alive.pop()!;

            this._removeParticle(particle);

            dead.push(particle);
        }

        this.container.update();

        return this;
    }

    forEachAlive(callback: (particle: Particle, emitter: Emitter) => void, context?: any): this {
        var alive = this.alive;

        for (var i = 0; i < alive.length; i++) {
            callback.call(context, alive[i], this);
        }

        return this;
    }

    forEachDead(callback: (particle: Particle, emitter: Emitter) => void, context?: any): this {
        var dead = this.dead;

        for (var i = 0; i < dead.length; i++) {
            callback.call(context, dead[i], this);
        }

        return this;
    }

    // ------------------------------------------------------------------
    // Start / stop / pause / resume
    // ------------------------------------------------------------------

    start(advance = 0, duration?: number): this {
        if (!this.emitting) {
            if (advance > 0) {
                this.fastForward(advance);
            }

            this.emitting = true;

            this.resetCounters(this.frequency, true);

            if (duration !== undefined) {
                this.duration = Math.abs(duration);
            }

            this.emit(Events.START, this);
        }

        return this;
    }

    stop(kill = false): this {
        if (this.emitting) {
            this.emitting = false;

            if (kill) {
                this.killAll();
            }

            this.emit(Events.STOP, this);
        }

        return this;
    }

    pause(): this {
        this.active = false;

        return this;
    }

    resume(): this {
        this.active = true;

        return this;
    }

    // ------------------------------------------------------------------
    // Sorting
    // ------------------------------------------------------------------

    setSortProperty(property = '', ascending = true): this {
        this.sortProperty = property;
        this.sortOrderAsc = ascending;
        this.sortCallback = this.depthSortCallback.bind(this);

        return this;
    }

    setSortCallback(callback?: (a: Particle, b: Particle) => number): this {
        this.sortCallback = this.sortProperty !== '' ? this.depthSortCallback.bind(this) : callback || null;

        return this;
    }

    depthSort(): this {
        if (!this.sortCallback) {
            return this;
        }

        StableSort(this.alive, this.sortCallback);

        var pc = this.container.particleChildren;

        pc.length = 0;

        for (var i = 0; i < this.alive.length; i++) {
            var pp = this.alive[i].pixiParticle;

            if (pp) {
                pc.push(pp);
            }
        }

        return this;
    }

    depthSortCallback(a: Particle, b: Particle): number {
        var key = this.sortProperty as keyof Particle;

        return this.sortOrderAsc ? (a[key] as any) - (b[key] as any) : (b[key] as any) - (a[key] as any);
    }

    // ------------------------------------------------------------------
    // Emission
    // ------------------------------------------------------------------

    flow(frequency: number, count = 1, stopAfter?: number): this {
        this.emitting = false;
        this.frequency = frequency;
        this.quantity = count;

        if (stopAfter !== undefined) {
            this.stopAfter = stopAfter;
        }

        return this.start();
    }

    explode(count?: number, x?: number, y?: number): Particle | undefined {
        this.frequency = -1;

        this.resetCounters(-1, true);

        var particle = this.emitParticle(count, x, y);

        this.emit(Events.EXPLODE, this, particle);

        return particle;
    }

    emitParticleAt(x?: number, y?: number, count?: number): Particle | undefined {
        return this.emitParticle(count, x, y);
    }

    emitParticle(count?: number, x?: number, y?: number): Particle | undefined {
        if (this.atLimit()) {
            return undefined;
        }

        if (count === undefined) {
            count = this.ops.quantity.onEmit();
        }

        var dead = this.dead;
        var stopAfter = this.stopAfter;

        var followX = this.follow ? this.follow.x + this.followOffset.x : x;
        var followY = this.follow ? this.follow.y + this.followOffset.y : y;

        var particle: Particle = undefined as any;
        var addedAny = false;

        for (var i = 0; i < count!; i++) {
            particle = dead.pop() ?? new this.particleClass(this);

            if (particle.fire(this.x, this.y)) {
                if (this.particleBringToTop) {
                    this.alive.push(particle);
                } else {
                    this.alive.unshift(particle);
                }

                this._addParticle(particle);
                addedAny = true;

                if (this.emitCallback) {
                    this.emitCallback.call(this.emitCallbackScope, particle, this);
                }
            } else {
                this.dead.push(particle);
            }

            if (stopAfter > 0) {
                this.stopCounter++;

                if (this.stopCounter >= stopAfter) {
                    break;
                }
            }

            if (this.atLimit()) {
                break;
            }
        }

        if (addedAny) {
            this.container.update();
        }

        return particle;
    }

    fastForward(time: number, delta = 1000 / 60): this {
        var total = 0;

        this.skipping = true;

        while (total < Math.abs(time)) {
            this.update(delta);

            total += delta;
        }

        this.skipping = false;

        return this;
    }

    // ------------------------------------------------------------------
    // Update loop — call this once per frame, e.g.
    //   gameTicker.add((t) => emitter.update(t.deltaMS))
    // ------------------------------------------------------------------

    update(deltaMS: number): void {
        if (!this.active) {
            return;
        }

        var delta = deltaMS * this.timeScale;
        var step = delta / 1000;

        if (this.trackVisible && this.follow) {
            this.visible = !!this.follow.visible;
        }

        this._updateEmitterMatrix();

        var processors = this.getProcessors();
        var particles = this.alive;
        var dead = this.dead;

        var rip: { index: number; particle: Particle }[] = [];

        for (var i = 0; i < particles.length; i++) {
            var particle = particles[i];

            if (particle.update(delta, step, processors)) {
                rip.push({ index: i, particle: particle });
            }
        }

        var removedAny = false;

        if (rip.length > 0) {
            var deathCallback = this.deathCallback;
            var deathCallbackScope = this.deathCallbackScope;

            for (var r = rip.length - 1; r >= 0; r--) {
                var entry = rip[r];

                particles.splice(entry.index, 1);
                dead.push(entry.particle);

                this._removeParticle(entry.particle);
                removedAny = true;

                if (deathCallback) {
                    deathCallback.call(deathCallbackScope, entry.particle);
                }

                entry.particle.setPosition();
            }
        }

        if (!this.emitting && !this.skipping) {
            if (this.completeFlag && particles.length === 0) {
                this.completeFlag = false;

                this.emit(Events.COMPLETE, this);
            }
        } else {
            if (this.frequency === 0) {
                this.emitParticle();
            } else if (this.frequency > 0) {
                this.flowCounter -= delta;

                while (this.flowCounter <= 0) {
                    this.emitParticle();

                    this.flowCounter += this.frequency;
                }
            }

            if (!this.skipping) {
                if (this.duration > 0) {
                    this.elapsed += delta;

                    if (this.elapsed >= this.duration) {
                        this.stop();
                    }
                }

                if (this.stopAfter > 0 && this.stopCounter >= this.stopAfter) {
                    this.stop();
                }
            }
        }

        if (this.sortProperty !== '' || this.sortCallback) {
            this.depthSort();
        }

        this._syncParticles();

        this.container.update();
    }

    // ------------------------------------------------------------------
    // Rendering (PIXI.Particle pool -> PIXI.ParticleContainer)
    // ------------------------------------------------------------------

    private _addParticle(particle: Particle): void {
        if (!particle.pixiParticle) {
            particle.pixiParticle = new PixiParticle({
                texture: particle.texture!,
                x: particle.x,
                y: particle.y,
                anchorX: 0.5,
                anchorY: 0.5
            });
        } else {
            particle.pixiParticle.texture = particle.texture;
        }

        if (this.particleBringToTop) {
            this.container.addParticle(particle.pixiParticle);
        } else {
            this.container.addParticleAt(particle.pixiParticle, 0);
        }
    }

    private _removeParticle(particle: Particle): void {
        if (particle.pixiParticle) {
            this.container.removeParticle(particle.pixiParticle);
        }
    }

    private _syncParticles(): void {
        var alive = this.alive;

        for (var i = 0; i < alive.length; i++) {
            var p = alive[i];
            var pp = p.pixiParticle;

            if (!pp) {
                continue;
            }

            pp.x = p.x;
            pp.y = p.y;
            pp.scaleX = p.scaleX;
            pp.scaleY = p.scaleY;
            pp.rotation = p.rotation;
            pp.alpha = p.alpha;
            pp.tint = p.tint;

            if (pp.texture !== p.texture) {
                pp.texture = p.texture;
            }
        }
    }

    /**
     * Recomputes the lightweight matrix used to turn a particle's local
     * (x, y) into "world" coordinates for zones / bounds. Only accounts for
     * this emitter's own x/y/rotation/scale — see the class doc comment.
     */
    private _updateEmitterMatrix(): void {
        var rotation = this.rotation;
        var cos = Math.cos(rotation);
        var sin = Math.sin(rotation);

        this._ma = cos * this.scaleX;
        this._mb = sin * this.scaleX;
        this._mc = -sin * this.scaleY;
        this._md = cos * this.scaleY;
        this._mtx = this.x;
        this._mty = this.y;
    }

    transformPoint(x: number, y: number, out: { x: number; y: number } = { x: 0, y: 0 }): { x: number; y: number } {
        var ox = this._ma * x + this._mc * y + this._mtx;
        var oy = this._mb * x + this._md * y + this._mty;

        out.x = ox;
        out.y = oy;

        return out;
    }

    // ------------------------------------------------------------------
    // Misc
    // ------------------------------------------------------------------

    getBounds(): { x: number; y: number; width: number; height: number } {
        var minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

        for (var i = 0; i < this.alive.length; i++) {
            var b = this.alive[i].getBounds();

            minX = Math.min(minX, b.x);
            minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.width);
            maxY = Math.max(maxY, b.y + b.height);
        }

        if (minX === Infinity) {
            return { x: this.x, y: this.y, width: 0, height: 0 };
        }

        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }

    destroy(): void {
        this.dead.forEach((p) => p.destroy());
        this.alive.forEach((p) => p.destroy());

        this.dead.length = 0;
        this.alive.length = 0;

        for (var key in this.ops) {
            this.ops[key].destroy();
        }

        this.processors.forEach((p) => p.destroy());
        this.processors.length = 0;

        this._events = {};
    }
}

export default Emitter;
